import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV } from "@/constants/testIds";

const links = [
  { label: "Services", route: "/services", testId: NAV.linkServices },
  { label: "About", route: "/about", testId: NAV.linkAbout },
  { label: "How We Work", route: "/how-we-work", testId: NAV.linkHowWeWork },
  { label: "Client Stories", route: "/client-stories", testId: NAV.linkClientStories },
  { label: "Resources", route: "/resources", testId: NAV.linkResources },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
      return () => {
        const y = document.body.style.top;
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.right = "";
        document.body.style.width = "";
        window.scrollTo(0, Math.abs(parseInt(y || "0", 10)));
      };
    }
  }, [open]);

  // Close menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const goTo = (link) => {
    setOpen(false);
    navigate(link.route);
  };

  const goContact = () => {
    setOpen(false);
    navigate("/contact");
  };

  const Logo = ({ big = false }) => (
    <button
      data-testid={NAV.logo}
      onClick={() => navigate("/")}
      className="flex items-center"
      aria-label="Waylio home"
    >
      <img
        src="/waylio-logo.png"
        alt="Waylio"
        className={big ? "h-14 md:h-16" : "h-12 md:h-14"}
        style={{ objectFit: "contain" }}
      />
    </button>
  );

  const MobileMenuOverlay = () =>
    createPortal(
      <motion.div
        data-testid={NAV.mobileMenu}
        initial={false}
        animate={{ opacity: 1 }}
        style={{ position: "fixed", inset: 0, zIndex: 90 }}
        className="md:hidden bg-paper overflow-y-auto"
      >
        <div className="h-16 px-6 flex items-center justify-between border-b border-forest/10">
          <Logo />
          <button
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            className="text-forest -mr-2 p-2"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="px-6 py-8 flex flex-col gap-2">
          {links.map((l) => (
            <button
              key={l.route}
              data-testid={l.testId}
              onClick={() => goTo(l)}
              className="text-left text-terracotta font-bold text-3xl py-4 border-b border-forest/10 hover:text-terracotta-hover transition-colors"
            >
              {l.label}
            </button>
          ))}
          <div className="mt-8 flex flex-col gap-3">
            <button
              onClick={goContact}
              data-testid={NAV.ctaContact}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-forest text-cream px-6 py-4 text-base font-bold"
            >
              Contact Us <ArrowUpRight className="w-5 h-5" />
            </button>
            <a
              href="tel:+14705804991"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-forest/25 text-forest px-6 py-4 text-base font-medium"
            >
              Call 470-580-4991
            </a>
            <a
              href="mailto:support@waylio.com"
              onClick={() => setOpen(false)}
              className="text-center text-charcoal/60 text-sm mt-2"
            >
              support@waylio.com
            </a>
          </div>
        </div>
      </motion.div>,
      document.body
    );

  return (
    <>
      <header
        className={`fixed top-9 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-paper/80 backdrop-blur-md border-b border-charcoal/10" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Logo big />

          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <button
                key={l.route}
                data-testid={l.testId}
                onClick={() => goTo(l)}
                className={`nav-link text-sm tracking-[0.02em] uppercase font-bold transition-colors ${
                  location.pathname === l.route ? "text-terracotta" : "text-forest hover:text-terracotta"
                }`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <button
            data-testid={NAV.ctaContact}
            onClick={goContact}
            className="hidden md:inline-flex items-center gap-2 rounded-full bg-forest text-cream px-5 py-2.5 text-sm font-bold hover:bg-terracotta transition-colors duration-300 group"
          >
            Contact Us
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>

          <button
            data-testid={NAV.mobileToggle}
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-forest p-2 -mr-2"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {open && <MobileMenuOverlay />}
    </>
  );
}
